import * as RadixRadioGroup from '@radix-ui/react-radio-group'
import React, { useState } from 'react'

import { cn } from '@sparkai/utils'

type Option = string | { value: string; displayValue: string; description?: string }

export type RadioGroupProps = {
  options: Option[]
  onSelectionChange: (selected: string | null) => void
  defaultSelection?: string | null
  value?: string
  className?: string
  render?: (option: string) => React.ReactElement
  index?: number
  disableTabNavigation?: boolean
}

export const RadioGroup = ({
  options,
  onSelectionChange,
  value,
  className,
  render,
  defaultSelection,
  index,
  disableTabNavigation = false,
}: RadioGroupProps) => {
  const normalizedOptions = options.map((option) => {
    if (typeof option === 'string') {
      return { value: option, displayValue: option }
    }
    return option
  })

  const isControlled = value !== undefined
  const [selected, setSelected] = useState<string | null | undefined>(value ?? defaultSelection)
  const selectedValue = isControlled ? value : selected

  const handleSelectChange = (newOption: string) => {
    if (selectedValue === newOption) {
      if (!isControlled) setSelected(undefined)
      onSelectionChange(null)
    } else {
      if (!isControlled) setSelected(newOption)
      onSelectionChange(newOption)
    }
  }

  return (
    <form>
      <RadixRadioGroup.Root
        className={className ?? 'flex gap-9'}
        value={selectedValue || ''}
        onValueChange={handleSelectChange}
      >
        {normalizedOptions.map((option) => {
          const { value, displayValue, description } = option
          return (
            <div className='flex flex-col' key={value}>
              <div className='relative flex items-start gap-2'>
                <div className='pt-[2px]'>
                  <RadixRadioGroup.Item
                    className={cn(
                      'relative flex h-4 min-h-4 w-4 min-w-4 items-center justify-center rounded-full border bg-white',
                      selectedValue === value ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    )}
                    value={value}
                    id={index ? `${value}_${index}` : value}
                    tabIndex={disableTabNavigation ? -1 : undefined}
                  >
                    {selectedValue === value && <div className='h-[6px] w-[6px] rounded-full bg-white' />}
                  </RadixRadioGroup.Item>
                </div>
                <div className='flex flex-col items-start'>
                  <label className='Label' htmlFor={index ? `${value}_${index}` : value}>
                    {displayValue.charAt(0).toUpperCase() + displayValue.slice(1)}
                  </label>
                  {description !== undefined && (
                    <p className='text-start text-sm font-normal text-gray-500'>{description}</p>
                  )}
                </div>
              </div>

              {render && render(value)}
            </div>
          )
        })}
      </RadixRadioGroup.Root>
    </form>
  )
}
